<template>
  <div class="rich-editor" :class="{ 'rich-editor--compact': compact, 'rich-editor--focused': isFocused, 'rich-editor--borderless': borderless }">
    <!-- Toolbar -->
    <EditorToolbar
      v-if="editor && showToolbar"
      :editor="editor"
      :compact="compact"
      class="rich-editor__toolbar"
    />

    <!-- Editor Content -->
    <div v-if="editor" class="rich-editor__content-wrapper">
      <EditorContent :editor="editor" class="rich-editor__content" />

      <!-- Character Count (only shown if maxLength and no footer slot) -->
      <div v-if="maxLength && !hasFooter" class="rich-editor__char-count">
        <span :class="{ 'rich-editor__char-count--over': characterCount > maxLength }">
          {{ characterCount }} / {{ maxLength }}
        </span>
      </div>
    </div>

    <!-- Loading placeholder -->
    <div v-else class="rich-editor__loading">
      <span>Loading editor...</span>
    </div>

    <!-- Media Upload Area -->
    <MediaUploadArea
      v-if="enableMedia"
      :images="mediaImages"
      :video="mediaVideo"
      :max-images="maxImages"
      :max-video-size="maxVideoSize"
      :disabled="disabled"
      class="rich-editor__media-area"
      @update:images="handleImagesUpdate"
      @update:video="handleVideoUpdate"
    />

    <!-- Footer (for media buttons, visibility, character count) -->
    <footer v-if="hasFooter" class="rich-editor__footer">
      <!-- Slot for visibility selector, etc. -->
      <slot name="footer-start" />

      <!-- Media actions -->
      <div v-if="enableMedia || enableEmoji" class="rich-editor__actions">
        <!-- Image upload -->
        <input
          ref="imageInputRef"
          type="file"
          accept="image/*"
          multiple
          hidden
          aria-hidden="true"
          @change="handleImageSelect"
        />
        <button
          v-if="enableMedia"
          class="rich-editor__action"
          type="button"
          title="Add images"
          aria-label="Add images"
          :disabled="!canAddImages"
          @click="imageInputRef?.click()"
        >
          <v-icon size="20">mdi-image-outline</v-icon>
        </button>

        <!-- Video upload -->
        <input
          ref="videoInputRef"
          type="file"
          accept="video/*"
          hidden
          aria-hidden="true"
          @change="handleVideoSelect"
        />
        <button
          v-if="enableMedia && videoEnabled"
          class="rich-editor__action"
          type="button"
          title="Add video"
          aria-label="Add video"
          :disabled="!canAddVideo"
          @click="videoInputRef?.click()"
        >
          <v-icon size="20">mdi-video-outline</v-icon>
        </button>

        <!-- Additional action buttons slot -->
        <slot name="footer-actions" />

        <!-- Emoji picker -->
        <EmojiPicker v-if="enableEmoji" @select="insertEmoji" />
      </div>

      <!-- Slot for character counter, etc. -->
      <slot name="footer-end">
        <div v-if="maxLength" class="rich-editor__footer-char-count">
          <CharacterCounter :current="characterCount" :max="maxLength" />
        </div>
      </slot>
    </footer>

    <!-- Suggestion Popup -->
    <SuggestionPopup
      v-if="suggestionState.active"
      :items="suggestionState.items"
      :loading="suggestionState.loading"
      :selected-index="suggestionState.selectedIndex"
      :type="suggestionState.type"
      :position="suggestionState.position"
      @select="handleSuggestionSelect"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount, useSlots } from "vue";
import { useEditor, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import EditorToolbar from "./EditorToolbar.vue";
import SuggestionPopup from "./SuggestionPopup.vue";
import MediaUploadArea from "./MediaUploadArea.vue";
import type { MediaPreview, VideoPreview } from "./types";
import CharacterCounter from "./CharacterCounter.vue";
import EmojiPicker from "@/components/composer/EmojiPicker.vue";
import { MentionExtension, type MentionSuggestionHandlers } from "./extensions/MentionExtension";
import { SongReferenceExtension } from "./extensions/SongReferenceExtension";
import { useSdk } from "@/composables/useSdk";
import type { JSONContent } from "@tiptap/vue-3";

interface SuggestionItem {
  id: string;
  label: string;
  avatar?: string;
  subtitle?: string;
}

interface SuggestionState {
  active: boolean;
  type: "mention" | "song";
  items: SuggestionItem[];
  loading: boolean;
  selectedIndex: number;
  position: { top: number; left: number };
  query: string;
  range: { from: number; to: number } | null;
}

const props = withDefaults(
  defineProps<{
    modelValue?: JSONContent | string;
    placeholder?: string;
    maxLength?: number;
    compact?: boolean;
    showToolbar?: boolean;
    autofocus?: boolean;
    borderless?: boolean;
    disabled?: boolean;
    // Media props
    enableMedia?: boolean;
    maxImages?: number;
    maxVideoSize?: number;
    videoEnabled?: boolean;
    enableEmoji?: boolean;
    // Submission behavior
    submitOnEnter?: boolean;
    submitOnCmdEnter?: boolean;
  }>(),
  {
    placeholder: "What's on your mind?",
    showToolbar: true,
    compact: false,
    autofocus: false,
    borderless: false,
    disabled: false,
    enableMedia: false,
    maxImages: 4,
    maxVideoSize: 500 * 1024 * 1024,
    videoEnabled: true,
    enableEmoji: false,
    submitOnEnter: false,
    submitOnCmdEnter: true,
  }
);

const emit = defineEmits<{
  "update:modelValue": [value: JSONContent];
  submit: [];
  focus: [];
  blur: [];
  "media-change": [images: MediaPreview[], video: VideoPreview | null];
}>();

const slots = useSlots();
const { sdk } = useSdk();
const isFocused = ref(false);

// Check if footer should be shown
const hasFooter = computed(() => {
  return props.enableMedia || props.enableEmoji || !!slots["footer-start"] || !!slots["footer-end"] || !!slots["footer-actions"];
});

// Media state
const mediaImages = ref<MediaPreview[]>([]);
const mediaVideo = ref<VideoPreview | null>(null);
const imageInputRef = ref<HTMLInputElement | null>(null);
const videoInputRef = ref<HTMLInputElement | null>(null);

const canAddImages = computed(() => {
  return mediaImages.value.length < props.maxImages && !mediaVideo.value && !props.disabled;
});

const canAddVideo = computed(() => {
  return !mediaVideo.value && mediaImages.value.length === 0 && !props.disabled;
});

// Media handlers
function handleImagesUpdate(images: MediaPreview[]) {
  mediaImages.value = images;
  emit("media-change", mediaImages.value, mediaVideo.value);
}

function handleVideoUpdate(video: VideoPreview | null) {
  mediaVideo.value = video;
  emit("media-change", mediaImages.value, mediaVideo.value);
}

function handleImageSelect(event: Event) {
  const input = event.target as HTMLInputElement;
  const files = input.files;
  if (files && files.length > 0) {
    handleImageFilesDirectly(files);
  }
  input.value = "";
}

function handleVideoSelect(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (file) {
    handleVideoFileDirectly(file);
  }
  input.value = "";
}

// Direct file handling - creates local previews only (uploads happen in background after submit)
function handleImageFilesDirectly(files: FileList) {
  const remainingSlots = props.maxImages - mediaImages.value.length;
  const newImages: MediaPreview[] = [];

  for (let i = 0; i < Math.min(files.length, remainingSlots); i++) {
    const file = files[i];
    if (file.type.startsWith("image/")) {
      const url = URL.createObjectURL(file);
      newImages.push({ url, file });
    }
  }

  if (newImages.length === 0) return;

  mediaImages.value = [...mediaImages.value, ...newImages];
  emit("media-change", mediaImages.value, mediaVideo.value);
}

// Creates local video preview only (upload happens in background after submit)
function handleVideoFileDirectly(file: File) {
  if (!file.type.startsWith("video/")) return;
  if (file.size > props.maxVideoSize) return;

  const url = URL.createObjectURL(file);
  mediaVideo.value = { url, file };
  emit("media-change", mediaImages.value, mediaVideo.value);
}

// Emoji insertion
function insertEmoji(emoji: string) {
  if (!editor.value) return;
  editor.value.chain().focus().insertContent(emoji).run();
}

// Suggestion state
const suggestionState = ref<SuggestionState>({
  active: false,
  type: "mention",
  items: [],
  loading: false,
  selectedIndex: 0,
  position: { top: 0, left: 0 },
  query: "",
  range: null,
});

// Debounce timer
let searchTimeout: ReturnType<typeof setTimeout> | null = null;

// Search functions
async function searchUsers(query: string): Promise<SuggestionItem[]> {
  if (query.length < 3) return [];

  try {
    const response = await sdk.searchUsersAutocomplete(query);
    const results = response.data || [];
    return results.slice(0, 5).map((user) => ({
      id: String(user.ulid || user.id || ""),
      label: user.username || "",
      avatar: user.avatar,
      subtitle: user.name,
    }));
  } catch (error) {
    console.error("Failed to search users:", error);
    return [];
  }
}

async function searchSongs(query: string): Promise<SuggestionItem[]> {
  if (query.length < 2) return [];

  try {
    const response = await sdk.searchAudio(query);
    const results = response.data || [];
    return results.slice(0, 5).map((song) => ({
      id: String(song.ulid || song.id || ""),
      label: song.title || "",
      avatar: (song as Record<string, unknown>).coverImage as string || (song as Record<string, unknown>).cover_image as string,
      subtitle: String(song.artist || ""),
    }));
  } catch (error) {
    console.error("Failed to search songs:", error);
    return [];
  }
}

// Handle suggestion search with debounce
function handleSuggestionSearch(type: "mention" | "song", query: string) {
  suggestionState.value.query = query;
  suggestionState.value.loading = true;
  suggestionState.value.selectedIndex = 0;

  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  searchTimeout = setTimeout(async () => {
    const searchFn = type === "mention" ? searchUsers : searchSongs;
    const items = await searchFn(query);
    suggestionState.value.items = items;
    suggestionState.value.loading = false;
  }, 200);
}

// Handle suggestion selection
function handleSuggestionSelect(item: SuggestionItem) {
  if (!editor.value || !suggestionState.value.range) return;

  const { type, range } = suggestionState.value;

  if (type === "mention") {
    editor.value
      .chain()
      .focus()
      .deleteRange(range)
      .insertContent({
        type: "mention",
        attrs: {
          id: item.id,
          label: item.label,
        },
      })
      .insertContent(" ")
      .run();
  } else {
    editor.value
      .chain()
      .focus()
      .deleteRange(range)
      .insertContent({
        type: "songReference",
        attrs: {
          id: item.id,
          title: item.label,
        },
      })
      .insertContent(" ")
      .run();
  }

  closeSuggestions();
}

function closeSuggestions() {
  suggestionState.value.active = false;
  suggestionState.value.items = [];
  suggestionState.value.query = "";
  suggestionState.value.range = null;
}

// Regex to match local song URLs
// Matches: /song/ULID or full URLs like https://epongo.net/song/ULID
const SONG_URL_REGEX = /(?:https?:\/\/[^/]+)?\/song\/([a-z0-9]{26})/gi;

// Extract song ULID from a URL
function extractSongUlid(text: string): string | null {
  const match = text.match(/\/song\/([a-z0-9]{26})/i);
  return match ? match[1] : null;
}

// Fetch song details and insert as song reference
async function handlePastedSongUrl(ulid: string): Promise<{ id: string; title: string } | null> {
  try {
    const post = await sdk.getPostByUlid(ulid);
    if (post && post.type?.toUpperCase() === 'SONG' && post.title) {
      return { id: ulid, title: post.title };
    }
  } catch (err) {
    console.warn('Failed to fetch song for URL:', err);
  }
  return null;
}

// Suggestion handlers object - will be passed to extension options
const suggestionHandlers: MentionSuggestionHandlers = {
  onStart: (suggestionProps) => {
    suggestionState.value.active = true;
    suggestionState.value.type = suggestionProps.type;
    suggestionState.value.range = suggestionProps.range;

    const rect = suggestionProps.clientRect?.();
    if (rect) {
      suggestionState.value.position = {
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      };
    }

    handleSuggestionSearch(suggestionProps.type, suggestionProps.query);
  },
  onUpdate: (suggestionProps) => {
    suggestionState.value.range = suggestionProps.range;

    const rect = suggestionProps.clientRect?.();
    if (rect) {
      suggestionState.value.position = {
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      };
    }

    handleSuggestionSearch(suggestionProps.type, suggestionProps.query);
  },
  onExit: () => {
    closeSuggestions();
  },
  onKeyDown: (keyProps) => {
    const { event } = keyProps;
    const { items, selectedIndex } = suggestionState.value;

    if (event.key === "ArrowUp") {
      event.preventDefault();
      suggestionState.value.selectedIndex = selectedIndex <= 0 ? items.length - 1 : selectedIndex - 1;
      return true;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      suggestionState.value.selectedIndex = selectedIndex >= items.length - 1 ? 0 : selectedIndex + 1;
      return true;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      if (items[selectedIndex]) {
        handleSuggestionSelect(items[selectedIndex]);
      }
      return true;
    }

    if (event.key === "Escape") {
      closeSuggestions();
      return true;
    }

    return false;
  },
};

// Create editor with suggestion handlers passed via extension options
const editor = useEditor({
  extensions: [
    StarterKit.configure({
      heading: false, // Disable headings
      codeBlock: false, // Disable code blocks
    }),
    Underline,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        rel: "nofollow noopener",
        target: "_blank",
      },
    }),
    Placeholder.configure({
      placeholder: props.placeholder,
    }),
    MentionExtension.configure({
      suggestion: suggestionHandlers,
    }),
    SongReferenceExtension.configure({
      suggestion: suggestionHandlers,
    }),
  ],
  content: typeof props.modelValue === "string" ? props.modelValue : props.modelValue || "",
  editable: !props.disabled,
  autofocus: props.autofocus,
  editorProps: {
    attributes: {
      class: "rich-editor__prose",
    },
    handleKeyDown: (view, event) => {
      // Handle Enter to submit (for message-style editors)
      if (props.submitOnEnter && event.key === "Enter" && !event.shiftKey && !event.metaKey && !event.ctrlKey) {
        // Don't submit if suggestion popup is active
        if (suggestionState.value.active) return false;
        emit("submit");
        return true;
      }
      // Handle Cmd/Ctrl+Enter to submit
      if (props.submitOnCmdEnter && event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
        emit("submit");
        return true;
      }
      return false;
    },
    handlePaste: (view, event) => {
      // Get pasted text
      const pastedText = event.clipboardData?.getData('text/plain') || '';

      // Check if it contains a song URL
      const songUlid = extractSongUlid(pastedText);
      if (!songUlid) {
        // Not a song URL, let default paste behavior handle it
        return false;
      }

      // Prevent default paste
      event.preventDefault();

      // Fetch song info and insert as reference (async)
      handlePastedSongUrl(songUlid).then((songInfo) => {
        if (songInfo && editor.value) {
          // Insert song reference node
          editor.value.chain().focus().insertContent({
            type: 'songReference',
            attrs: {
              id: songInfo.id,
              title: songInfo.title,
            },
          }).insertContent(' ').run();
        } else if (editor.value) {
          // Fallback: insert the original text if song lookup failed
          editor.value.chain().focus().insertContent(pastedText).run();
        }
      });

      return true;
    },
  },
  onUpdate: ({ editor: ed }) => {
    emit("update:modelValue", ed.getJSON());
  },
  onFocus: () => {
    isFocused.value = true;
    emit("focus");
  },
  onBlur: () => {
    isFocused.value = false;
    emit("blur");
  },
});

// Character count
const characterCount = computed(() => {
  if (!editor.value) return 0;
  return editor.value.storage.characterCount?.characters() || editor.value.getText().length;
});

// Watch for external model changes
watch(
  () => props.modelValue,
  (newValue) => {
    if (!editor.value) return;

    const currentJSON = JSON.stringify(editor.value.getJSON());
    const newJSON = JSON.stringify(newValue);

    if (currentJSON !== newJSON) {
      editor.value.commands.setContent(newValue || "");
    }
  }
);

// Watch for disabled changes
watch(
  () => props.disabled,
  (disabled) => {
    if (editor.value) {
      editor.value.setEditable(!disabled);
    }
  }
);

// Cleanup
onBeforeUnmount(() => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }
  // Clean up media URLs
  mediaImages.value.forEach((m) => URL.revokeObjectURL(m.url));
  if (mediaVideo.value) {
    URL.revokeObjectURL(mediaVideo.value.url);
  }
  editor.value?.destroy();
});

// Expose editor for parent components
defineExpose({
  editor,
  focus: () => editor.value?.commands.focus(),
  clear: () => {
    editor.value?.commands.clearContent();
    // Also clear media
    mediaImages.value.forEach((m) => URL.revokeObjectURL(m.url));
    mediaImages.value = [];
    if (mediaVideo.value) {
      URL.revokeObjectURL(mediaVideo.value.url);
      mediaVideo.value = null;
    }
    emit("media-change", [], null);
  },
  isEmpty: () => editor.value?.isEmpty ?? true,
  getJSON: () => editor.value?.getJSON(),
  getText: () => editor.value?.getText() ?? "",
  getHTML: () => editor.value?.getHTML() ?? "",
  // Media accessors
  getImages: () => mediaImages.value,
  getVideo: () => mediaVideo.value,
  isUploading: () => false,
  allMediaUploaded: () => true,
  characterCount,
});
</script>

<style scoped lang="scss">
.rich-editor {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 12px;
  background: rgba(var(--v-theme-surface), 1);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &--focused {
    border-color: rgb(var(--v-theme-primary));
    box-shadow: 0 0 0 1px rgb(var(--v-theme-primary));
  }

  &--borderless {
    border: none;
    border-radius: 0;
    box-shadow: none;

    &.rich-editor--focused {
      box-shadow: none;
    }
  }

  &--compact {
    .rich-editor__content {
      min-height: 60px;
    }
  }

  &__toolbar {
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  }

  &__content-wrapper {
    position: relative;
  }

  &__content {
    min-height: 100px;
    max-height: 300px;
    overflow-y: auto;
    padding: 12px;

    :deep(.ProseMirror) {
      outline: none;
      min-height: inherit;

      p {
        margin: 0 0 0.5em 0;

        &:last-child {
          margin-bottom: 0;
        }
      }

      ul,
      ol {
        margin: 0.5em 0;
        padding-left: 1.5em;
      }

      blockquote {
        border-left: 3px solid rgba(var(--v-theme-primary), 0.5);
        margin: 0.5em 0;
        padding-left: 1em;
        color: rgba(var(--v-theme-on-surface), 0.7);
      }

      a {
        color: rgb(var(--v-theme-primary));
        text-decoration: underline;
      }

      .mention,
      .song-reference {
        background: rgba(var(--v-theme-primary), 0.15);
        color: rgb(var(--v-theme-primary));
        padding: 2px 4px;
        border-radius: 4px;
        font-weight: 500;
      }

      .is-editor-empty:first-child::before {
        content: attr(data-placeholder);
        float: left;
        color: rgba(var(--v-theme-on-surface), 0.4);
        pointer-events: none;
        height: 0;
      }
    }
  }

  &__media-area {
    padding: 0 12px 12px;
  }

  &__char-count {
    position: absolute;
    bottom: 8px;
    right: 12px;
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.5);

    &--over {
      color: rgb(var(--v-theme-error));
    }
  }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 60px;
    color: rgba(var(--v-theme-on-surface), 0.4);
    font-size: 0.875rem;
  }

  &__footer {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-top: 1px solid rgba(var(--v-theme-on-surface), 0.06);
    flex-wrap: wrap;
  }

  &__actions {
    display: flex;
    gap: 4px;
    flex: 1;
  }

  &__action {
    width: 36px;
    height: 36px;
    border: none;
    background: transparent;
    border-radius: 50%;
    color: #f59e0b;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      background: rgba(245, 158, 11, 0.1);
    }

    &:disabled {
      color: rgba(var(--v-theme-on-surface), 0.4);
      cursor: not-allowed;
    }

    &:focus-visible {
      outline: 2px solid #f59e0b;
      outline-offset: 2px;
    }
  }

  &__footer-char-count {
    display: flex;
    align-items: center;
  }
}

// Light theme
.v-theme--light {
  .rich-editor {
    &__action {
      color: #d97706;

      &:hover:not(:disabled) {
        background: rgba(217, 119, 6, 0.1);
      }
    }
  }
}
</style>
